import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';

export default function AdminSupportTab() {
  const [tickets, setTickets] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [filter, setFilter] = useState('open');

  useEffect(() => {
    const q = query(collection(db, 'support_tickets'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleReplyChange = (ticketId, value) => {
    setReplyText(prev => ({ ...prev, [ticketId]: value }));
  };

  const submitReply = async (ticket) => {
    const reply = replyText[ticket.id];
    if (!reply) return toast.error("Please type a reply");

    const loadingToast = toast.loading("Transmitting response...");
    try {
      // 1. Update the ticket itself
      const ticketRef = doc(db, 'support_tickets', ticket.id);
      await updateDoc(ticketRef, {
        adminReply: reply,
        status: 'resolved',
        lastUpdated: serverTimestamp()
      });

      // 2. Push notification to the user's private feed
      if (ticket.userId) {
        await addDoc(collection(db, 'users', ticket.userId, 'notifications'), {
          title: "Support Update",
          body: `Admin replied to: "${ticket.subject}". Status: RESOLVED.`,
          type: 'support_reply',
          timestamp: serverTimestamp(),
          read: false,
          link: '/support' // Assuming they have a support page to view history
        });
      }

      toast.success("Reply sent & User notified", { id: loadingToast });
      setReplyText(prev => ({ ...prev, [ticket.id]: '' }));
    } catch (err) {
      toast.error("Transmission failed", { id: loadingToast });
    }
  };

  const reopenTicket = async (ticketId) => {
    try {
      await updateDoc(doc(db, 'support_tickets', ticketId), { 
        status: 'open',
        lastUpdated: serverTimestamp() 
      });
      toast.success("Ticket re-opened for investigation");
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  // --- STYLES ---
  const containerStyle = {
    color: 'white'
  };
  const filterBar = { display: 'flex', gap: '10px', marginBottom: '20px' };
  const filterBtn = (active) => ({
    background: active ? GOLD : 'rgba(255,255,255,0.05)',
    color: active ? 'black' : 'white',
    border: active ? 'none' : '1px solid rgba(255,255,255,0.1)',
    padding: '10px 25px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '0.7rem',
    letterSpacing: '1px',
    transition: '0.3s'
  });
  const ticketCard = { 
    background: '#0a0a0a', 
    border: '1px solid #1a1a1a', 
    borderRadius: '20px', 
    padding: '25px', 
    marginBottom: '20px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
  };
  const categoryBadge = (cat) => ({
    background: cat === 'Payment' ? '#2ecc7133' : '#3498db33',
    color: cat === 'Payment' ? '#2ecc71' : '#3498db',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '0.6rem',
    fontWeight: 'bold',
    marginLeft: '10px',
    border: `1px solid ${cat === 'Payment' ? '#2ecc7155' : '#3498db55'}`
  });
  const inputStyle = { 
    width: '100%', 
    background: '#000', 
    border: '1px solid #222', 
    padding: '15px', 
    borderRadius: '12px', 
    color: 'white', 
    marginTop: '15px',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const filteredTickets = tickets.filter(t => t.status === filter);

  return (
    <div style={containerStyle}>
      <header style={{ marginBottom: '30px' }}>
        <h2 style={{ color: GOLD, fontSize: '1.5rem', margin: 0, fontWeight: '900' }}>SUPPORT COMMAND</h2>
        <p style={{ opacity: 0.5, fontSize: '0.75rem', marginTop: '5px' }}>Direct interface for user dispute resolution</p>
      </header>

      <div style={filterBar}>
        <button style={filterBtn(filter === 'open')} onClick={() => setFilter('open')}>
          OPEN ISSUES ({tickets.filter(t => t.status === 'open').length})
        </button>
        <button style={filterBtn(filter === 'resolved')} onClick={() => setFilter('resolved')}>
          RESOLVED ARCHIVE
        </button>
      </div>

      {filteredTickets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '100px 0', opacity: 0.2 }}>
          <h3 style={{ letterSpacing: '2px' }}>CLEAN QUEUE: NO {filter.toUpperCase()} TICKETS</h3>
        </div>
      ) : (
        filteredTickets.map(ticket => (
          <div key={ticket.id} style={ticketCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.6rem', color: GOLD, fontWeight: 'bold', letterSpacing: '1px' }}>
                    ID: {ticket.id.slice(0,8).toUpperCase()} | FROM: {ticket.userName || 'Unknown'}
                  </span>
                  {ticket.category && <span style={categoryBadge(ticket.category)}>{ticket.category.toUpperCase()}</span>}
                </div>
                <h3 style={{ margin: '8px 0', fontSize: '1.1rem', color: '#fff' }}>{ticket.subject}</h3>
                <span style={{ fontSize: '0.7rem', color: '#666' }}>{ticket.userEmail}</span>
              </div>
              <span style={{ fontSize: '0.65rem', color: '#444' }}>
                {ticket.createdAt?.toDate().toLocaleString()}
              </span>
            </div>

            <div style={{ background: '#000', padding: '20px', borderRadius: '15px', border: '1px solid #111' }}>
              <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.6', color: '#ccc' }}>{ticket.message}</p>
              
              {/* ATTACHMENT SUPPORT */}
              {ticket.imageUrl && (
                <div style={{ marginTop: '15px' }}>
                  <p style={{ fontSize: '0.6rem', color: GOLD, marginBottom: '5px' }}>ATTACHED EVIDENCE:</p>
                  <img 
                    src={ticket.imageUrl} 
                    alt="Evidence" 
                    style={{ maxWidth: '200px', borderRadius: '10px', cursor: 'pointer', border: '1px solid #222' }} 
                    onClick={() => window.open(ticket.imageUrl, '_blank')}
                  />
                </div>
              )}
            </div>

            {ticket.status === 'open' ? (
              <div style={{ marginTop: '20px' }}>
                <textarea 
                  style={inputStyle} 
                  rows="3"
                  placeholder="Draft your official response..." 
                  value={replyText[ticket.id] || ''}
                  onChange={(e) => handleReplyChange(ticket.id, e.target.value)}
                />
                <button 
                  style={{ background: GOLD, color: 'black', border: 'none', padding: '12px 25px', borderRadius: '10px', marginTop: '15px', fontWeight: '900', cursor: 'pointer', fontSize: '0.75rem' }} 
                  onClick={() => submitReply(ticket)}
                >
                  TRANSMIT REPLY & CLOSE TICKET
                </button>
              </div>
            ) : (
              <div style={{ marginTop: '20px', padding: '20px', background: 'rgba(184, 134, 11, 0.05)', borderRadius: '15px', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <p style={{ color: GOLD, fontWeight: 'bold', fontSize: '0.65rem', margin: '0 0 8px', letterSpacing: '1px' }}>SYSTEM RESPONSE ARCHIVE:</p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#aaa', fontStyle: 'italic' }}>"{ticket.adminReply}"</p>
                <button 
                  style={{ background: 'none', border: 'none', color: '#666', fontSize: '0.7rem', cursor: 'pointer', marginTop: '15px', fontWeight: 'bold', textDecoration: 'underline' }}
                  onClick={() => reopenTicket(ticket.id)}
                >
                  RE-OPEN DISPUTE
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
